import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { DS2 } from '../theme/designTokens';
import { AuthPageShell } from '../components/ui/AuthPageShell';
import { AuthBrandMark } from '../components/ui/AuthBrandMark';
import { AuthField } from '../components/ui/AuthField';
import { AuthSubmitButton } from '../components/ui/AuthSubmitButton';
import { AuthHeading } from '../components/ui/AuthHeading';
import { AuthBody } from '../components/ui/AuthBody';
import { PasswordVisibilityToggle } from '../components/ui/PasswordVisibilityToggle';
import { LOGIN_GLOW_1, LOGIN_GLOW_2 } from './LoginScreen';
import { requestPasswordReset, verifyPasswordReset, completePasswordReset } from '../utils/firebase';
import { useTranslation } from '../i18n/react';

/**
 * FORGOT PASSWORD — Figma "reset password" / "create new password" /
 * "password-success" frames, 2560×1321 export, values ×0.75 (same scale
 * convention as Login/GetStartedScreen). Lives inside the DS2 shell
 * (Sidebar on desktop, DS2 top bar on mobile), sharing Login's approved
 * AuthPageShell/AuthField/AuthSubmitButton primitives.
 *
 * Real Firebase link-based reset flow (see utils/firebase.ts):
 *   1. ForgotPasswordScreen        — request a reset email (Figma "reset password")
 *   2. ForgotPasswordSentScreen    — "check your email" (NO Figma — adapted
 *      from the approved Auth-family pattern; see component docstring)
 *   3. ForgotPasswordNewScreen     — the page Firebase's emailed link lands
 *      on (?mode=resetPassword&oobCode=…). Verifies the code, then shows
 *      Figma "create new password"; an invalid/expired/missing code shows
 *      an adapted state (NO Figma supplied) instead of the form.
 *   4. ForgotPasswordSuccessScreen — Figma "password-success"
 *
 * The old 4-digit OTP step (ForgotPasswordCodeScreen) is gone — Firebase's
 * password reset never issued a numeric code; that screen was never wired
 * to anything real. /forgot-password/code now redirects to /forgot-password
 * (see App.tsx) rather than 404ing for any stale links/bookmarks.
 *
 * DEPLOYMENT DEPENDENCY: for a real emailed link to open
 * ForgotPasswordNewScreen with a usable oobCode, the Firebase Console's
 * password-reset email template must have its action URL customized to
 * point at this app's own /forgot-password/new-password route (Console →
 * Authentication → Templates → Password reset → "Customize action URL").
 * Without that, Firebase's default hosted reset page handles the link
 * itself and this screen is never reached. This is a Firebase Console
 * config change, not app code — flagged for the user to configure/confirm.
 */

// create-new-password / password-success share this glow2 (Figma-exact —
// distinct from Login/email-entry's glow2, per the supplied CSS).
const RESET_GLOW_2 = { width: 1207.5, height: 935.25, top: -208.5, leftOffset: -204.75, radius: 0 };

function FieldError({ message }: { message: string }) {
  if (!message) return null;
  return (
    <p style={{ margin: 0, fontFamily: DS2.font.body, fontSize: 12.6, color: '#f87171', textAlign: 'center' }}>
      {message}
    </p>
  );
}

// ─── Step 1: request reset email — Figma "reset password" ─────────────────

export function ForgotPasswordScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [email,   setEmail]   = useState('');
  // Error state holds a translation KEY so it re-renders on language switch.
  const [error,   setError]   = useState<'forgot.tooMany' | 'forgot.errorGeneric' | ''>('');
  const [loading, setLoading] = useState(false);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      await requestPasswordReset(email.trim());
      navigate('/forgot-password/sent', { state: { email: email.trim() } });
    } catch (err: unknown) {
      const fe = err as { code?: string };
      // Do not reveal whether an account exists for this email — both a real
      // account and auth/user-not-found route to the same generic
      // confirmation. Only surface genuine failures (bad format slipping
      // past client validation, rate limiting, network errors).
      if (fe.code === 'auth/user-not-found' || fe.code === 'auth/invalid-email') {
        navigate('/forgot-password/sent', { state: { email: email.trim() } });
        return;
      }
      if (fe.code === 'auth/too-many-requests') {
        setError('forgot.tooMany');
      } else {
        setError('forgot.errorGeneric');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthPageShell glow1={LOGIN_GLOW_1} glow2={LOGIN_GLOW_2}>
      <AuthBrandMark />
      <AuthHeading>{t('forgot.heading')}</AuthHeading>
      <AuthBody>
        {t('forgot.body')}
      </AuthBody>

      <form onSubmit={handleSubmit} style={{ width: '100%', marginTop: 24, display: 'flex', flexDirection: 'column', gap: 15 }}>
        <AuthField label={t('login.email')} value={email} onChange={setEmail} type="email" autoComplete="email" />

        <FieldError message={error ? t(error) : ''} />

        <div style={{ marginTop: 3 }}>
          {/* Figma's own "Log In" label on this button is a copy-paste
              artifact from the Login CTA component — this control sends a
              reset email, it doesn't log anyone in, so the label is
              corrected to describe the actual action. */}
          <AuthSubmitButton loading={loading} loadingLabel={t('forgot.sending')} disabled={!isValidEmail}>
            {t('forgot.submit')}
          </AuthSubmitButton>
        </div>
      </form>
    </AuthPageShell>
  );
}

// ─── Step 2: "check your email" — NO Figma design supplied for this state.
// Adapted from the approved Auth-family pattern (same shell/brand
// mark/heading/body/CTA as the rest of this family) rather than the old
// AuthShellV2 envelope-icon version. Preserves the existing behavior (this
// state already existed functionally); only its visual presentation is new. ─

export function ForgotPasswordSentScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  return (
    <AuthPageShell glow1={LOGIN_GLOW_1} glow2={LOGIN_GLOW_2}>
      <AuthBrandMark />
      <AuthHeading>{t('forgot.sentHeading')}</AuthHeading>
      <AuthBody>
        {t('forgot.sentBody')}
      </AuthBody>

      <div style={{ width: '100%', marginTop: 24 }}>
        <button
          type="button"
          onClick={() => navigate('/login', { replace: true })}
          style={{
            width: '100%', height: 39, boxSizing: 'border-box', display: 'flex', alignItems: 'center',
            justifyContent: 'center', borderRadius: 11.25, background: DS2.gradient.gold,
            border: '0.75px solid #C9A84C', boxShadow: '0px 22.5px 60px -22.5px rgba(201, 168, 76, 0.45)',
            color: '#FFFFFF', fontFamily: DS2.font.body, fontWeight: 500, fontSize: 16.5, cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t('forgot.backToLogin')}
        </button>
      </div>
    </AuthPageShell>
  );
}

// ─── Step 3: the real reset-link landing page — Figma "create new password".
// Reached via Firebase's emailed link (?mode=resetPassword&oobCode=…). The
// invalid/expired/missing-code state has NO supplied Figma design — adapted
// from the approved Auth-family pattern, disclosed here and in the PR report. ─

type LinkState = 'verifying' | 'valid' | 'invalid';

export function ForgotPasswordNewScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');
  const mode    = searchParams.get('mode');

  const [linkState, setLinkState] = useState<LinkState>('verifying');
  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [showPw,    setShowPw]    = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [error,     setError]     = useState<'forgot.weakPassword' | 'forgot.errorGeneric' | ''>('');
  const [loading,   setLoading]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!oobCode || mode !== 'resetPassword') {
      setLinkState('invalid');
      return;
    }
    verifyPasswordReset(oobCode)
      .then(() => { if (!cancelled) setLinkState('valid'); })
      .catch(() => { if (!cancelled) setLinkState('invalid'); });
    return () => { cancelled = true; };
  }, [oobCode, mode]);

  const isValid = password.length >= 8 && password === confirm;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading || !oobCode) return;
    setError('');
    setLoading(true);
    try {
      await completePasswordReset(oobCode, password);
      navigate('/forgot-password/success', { replace: true });
    } catch (err: unknown) {
      const fe = err as { code?: string };
      if (fe.code === 'auth/weak-password') {
        setError('forgot.weakPassword');
      } else if (fe.code === 'auth/expired-action-code' || fe.code === 'auth/invalid-action-code') {
        setLinkState('invalid');
      } else {
        setError('forgot.errorGeneric');
      }
    } finally {
      setLoading(false);
    }
  }

  if (linkState === 'verifying') {
    return (
      <AuthPageShell glow1={LOGIN_GLOW_1} glow2={RESET_GLOW_2}>
        <AuthBrandMark />
        <AuthHeading>{t('forgot.verifying')}</AuthHeading>
      </AuthPageShell>
    );
  }

  if (linkState === 'invalid') {
    return (
      <AuthPageShell glow1={LOGIN_GLOW_1} glow2={RESET_GLOW_2}>
        <AuthBrandMark />
        <AuthHeading>{t('forgot.expiredHeading')}</AuthHeading>
        <AuthBody>
          {t('forgot.expiredBody')}
        </AuthBody>
        <div style={{ width: '100%', marginTop: 24 }}>
          <Link
            to="/forgot-password"
            style={{
              width: '100%', height: 39, boxSizing: 'border-box', display: 'flex', alignItems: 'center',
              justifyContent: 'center', borderRadius: 11.25, background: DS2.gradient.gold,
              border: '0.75px solid #C9A84C', boxShadow: '0px 22.5px 60px -22.5px rgba(201, 168, 76, 0.45)',
              color: '#FFFFFF', fontFamily: DS2.font.body, fontWeight: 500, fontSize: 16.5, textDecoration: 'none',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {t('forgot.requestNew')}
          </Link>
        </div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell glow1={LOGIN_GLOW_1} glow2={RESET_GLOW_2}>
      <AuthBrandMark />
      <AuthHeading>{t('forgot.newHeading')}</AuthHeading>
      <AuthBody>{t('forgot.newBody')}</AuthBody>

      <form onSubmit={handleSubmit} style={{ width: '100%', marginTop: 24, display: 'flex', flexDirection: 'column', gap: 15 }}>
        <AuthField
          label={t('forgot.createPassword')}
          value={password}
          onChange={setPassword}
          type={showPw ? 'text' : 'password'}
          autoComplete="new-password"
          rightSlot={<PasswordVisibilityToggle visible={showPw} onToggle={() => setShowPw(v => !v)} field="passwordField" />}
        />
        <AuthField
          label={t('forgot.reenterPassword')}
          value={confirm}
          onChange={setConfirm}
          type={showConfirmPw ? 'text' : 'password'}
          autoComplete="new-password"
          rightSlot={<PasswordVisibilityToggle visible={showConfirmPw} onToggle={() => setShowConfirmPw(v => !v)} field="passwordConfirmField" />}
        />

        <FieldError message={error ? t(error) : ''} />

        <div style={{ marginTop: 3 }}>
          {/* Figma's "Log In" label here is the same copy-paste artifact as
              the request step — corrected to describe the actual action. */}
          <AuthSubmitButton loading={loading} loadingLabel={t('forgot.saving')} disabled={!isValid}>
            {t('forgot.resetSubmit')}
          </AuthSubmitButton>
        </div>
      </form>
    </AuthPageShell>
  );
}

// ─── Step 4: success — Figma "password-success" ────────────────────────────

export function ForgotPasswordSuccessScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  return (
    <AuthPageShell glow1={LOGIN_GLOW_1} glow2={RESET_GLOW_2} maxWidth={720}>
      <div
        style={{
          width: 90, height: 90, borderRadius: '50%', background: DS2.gradient.gold,
          boxShadow: '0px 6px 18px rgba(201, 168, 76, 0.27)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 12l6 6L20 6" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <h1
        style={{
          margin: '24px 0 0', fontFamily: DS2.font.heading, fontWeight: 700, fontSize: 33, lineHeight: '39px',
          letterSpacing: '-0.6px', textAlign: 'center', color: '#FFFFFF',
        }}
      >
        {t('forgot.successHeading')}
      </h1>
      <p
        style={{
          margin: '16px 0 0', fontFamily: DS2.font.body, fontWeight: 300, fontSize: 22.5, lineHeight: '33px',
          textAlign: 'center', color: DS2.color.textBright,
        }}
      >
        {t('forgot.successBody')}
      </p>

      <div style={{ width: '100%', maxWidth: 363, marginTop: 29 }}>
        <button
          type="button"
          onClick={() => navigate('/login', { replace: true })}
          style={{
            width: '100%', height: 43.5, boxSizing: 'border-box', display: 'flex', alignItems: 'center',
            justifyContent: 'center', borderRadius: 11.25, background: DS2.gradient.gold,
            border: '0.75px solid #C9A84C', boxShadow: '0px 22.5px 60px -22.5px rgba(201, 168, 76, 0.45)',
            color: '#FFFFFF', fontFamily: DS2.font.body, fontWeight: 500, fontSize: 16.5, cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t('forgot.logIn')}
        </button>
      </div>
    </AuthPageShell>
  );
}
